// FakeNewsSkeletonCard.js
import React, { useEffect, useState } from "react";
import {
  Skeleton,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Fade,
} from "@mui/material";

const FakeNewsSkeletonCard = ({ loading }) => {
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showButtons, setShowButtons] = useState(false);

  // Staggered reveal effect
  useEffect(() => {
    if (loading) {
      setShowAnalysis(false);
      setShowButtons(false);

      const analysisTimer = setTimeout(() => setShowAnalysis(true), 250);
      const buttonsTimer = setTimeout(() => setShowButtons(true), 400);

      return () => {
        clearTimeout(analysisTimer);
        clearTimeout(buttonsTimer);
      };
    }
  }, [loading]);

  // Utility: random text width (simulate real paragraphs)
  const randomWidth = (min, max) =>
    `${Math.floor(Math.random() * (max - min) + min)}%`;

  return (
    <Fade in={loading} timeout={400}>
      <Card
        sx={{
          borderRadius: "12px",
          padding: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          width: "100%",
        }}
      >
        {/* Image Placeholder */}

        {/* Image Placeholder with centered text */}
        <Box
          sx={{
            position: "relative",
            height: 180,
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {/* Skeleton background */}
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{ width: "100%", height: "100%" }}
          />

          {/* Centered overlay */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              bgcolor: "rgba(255,255,255,0.6)", // optional translucent overlay
            }}
          >
            <CircularProgress size={20} thickness={4} />
            <span
              style={{
                fontSize: "14px",
                color: "#444",
                marginTop: "8px",
                fontFamily: '"Host Grotesk", sans-serif',
              }}
            >
              Analyzing your claim…
            </span>
          </Box>
        </Box>

        <CardContent>
          {/* Claim text */}
          <Skeleton
            variant="text"
            width={randomWidth(75, 95)}
            height={20}
            animation="wave"
          />
          <Skeleton
            variant="text"
            width={randomWidth(55, 85)}
            height={20}
            animation="wave"
          />

          {/* Verdict badge */}
          <Box mt={2} display="flex" alignItems="center" gap={1}>
            <Skeleton
              variant="circular"
              width={24}
              height={24}
              animation="pulse"
            />
            <Skeleton variant="text" width={80} height={20} animation="wave" />
          </Box>

          {/* Fact Analysis box (staggered reveal) */}
          {showAnalysis && (
            <Box
              mt={2}
              p={2}
              sx={{
                border: "1px solid #eee",
                borderRadius: "6px",
                backgroundColor: "#fafafa",
                minHeight: "140px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-around",
              }}
            >
              {[1, 2, 3, 4].map((i) => (
                <Skeleton
                  key={i}
                  variant="text"
                  width={randomWidth(60, 95)}
                  height={14}
                  animation="wave"
                />
              ))}
            </Box>
          )}

          {/* Meta info (Date + ID) */}
          <Box mt={2} display="flex" justifyContent="space-between">
            <Skeleton variant="text" width="40%" height={14} animation="wave" />
            <Skeleton variant="text" width="30%" height={14} animation="wave" />
          </Box>

          {/* Sources accordion */}
          <Box mt={2}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={35}
              animation="pulse"
              sx={{ borderRadius: "6px" }}
            />
          </Box>
          {/* Related Social Media (chips skeleton) */}
          {/* Related Social Media (chips skeleton, irregular layout) */}
          <Box mt={2} display="flex" flexDirection="column" gap={1}>
            {/* Row 1: one wide chip */}
            <Box display="flex" gap={1}>
              <Skeleton
                variant="rectangular"
                width="70%"
                height={28}
                animation="pulse"
                sx={{ borderRadius: "16px" }}
              />
            </Box>

            {/* Row 2: two small chips */}
            <Box display="flex" gap={1}>
              <Skeleton
                variant="rectangular"
                width="30%"
                height={28}
                animation="pulse"
                sx={{ borderRadius: "16px" }}
              />
              <Skeleton
                variant="rectangular"
                width="35%"
                height={28}
                animation="pulse"
                sx={{ borderRadius: "16px" }}
              />
            </Box>

            {/* Row 3: medium chip */}
            <Box display="flex" gap={1}>
              <Skeleton
                variant="rectangular"
                width="50%"
                height={28}
                animation="pulse"
                sx={{ borderRadius: "16px" }}
              />
            </Box>

            {/* Row 4: smaller + tiny chip */}
            <Box display="flex" gap={1}>
              <Skeleton
                variant="rectangular"
                width="40%"
                height={28}
                animation="pulse"
                sx={{ borderRadius: "16px" }}
              />
              <Skeleton
                variant="rectangular"
                width="20%"
                height={28}
                animation="pulse"
                sx={{ borderRadius: "16px" }}
              />
            </Box>
          </Box>

          {/* Buttons (staggered reveal) */}
          {showButtons && (
            <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
              <Skeleton
                variant="rectangular"
                width={100}
                height={36}
                animation="pulse"
                sx={{ borderRadius: "20px" }}
              />
              <Skeleton
                variant="rectangular"
                width={100}
                height={36}
                animation="pulse"
                sx={{ borderRadius: "20px" }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Fade>
  );
};

export default FakeNewsSkeletonCard;
